"use client";

import React, { Suspense, useContext } from "react";

import { LazyEncapsulatedQuickAdd } from "@faire/retailer/components/QuickAdd/LazyEncapsulatedQuickAdd";
import { QuickAddProps } from "@faire/retailer/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/components/QuickAdd/QuickAddProps";
import { QuickAddTrackingEventsContext } from "@faire/retailer/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/components/QuickAdd/QuickAddTrackingEventsContext/QuickAddTrackingEventsContext";
import { QuickAddTrackingEventsContextProvider } from "@faire/retailer/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/components/QuickAdd/QuickAddTrackingEventsContext/QuickAddTrackingEventsContextProvider";
import { canQuickAdd } from "@faire/retailer/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/useCanQuickAdd";
import { useBrandPageCartStateContext } from "@faire/retailer/views/brand/contexts/BrandPageCartStateContext";

const BrandCartContextAwareQuickAddBase = ({
  data: { productToken, quickAdd, brandToken, state },
}: {
  data: QuickAddProps;
}) => {
  const canQuickAddProduct = canQuickAdd(state, quickAdd);
  const { brandCartViewState } = useBrandPageCartStateContext();

  const {
    onQuickAddButtonClick,
    onQuickAddSubmit,
    onChooseOptionClick,
    onChooseOptionQuantityUpdateSubmit,
  } = useContext(QuickAddTrackingEventsContext);
  if (!canQuickAddProduct) {
    return null;
  }

  return (
    <div data-test-id="composable-quick-add">
      <Suspense fallback={null} key={`${productToken}-suspense`}>
        <LazyEncapsulatedQuickAdd
          key={`${productToken}-encapsulated-quick-add`}
          productToken={productToken}
          quickAdd={quickAdd}
          brandToken={brandToken}
          updateItems={brandCartViewState.updateItems}
          onQuickAddClick={onQuickAddButtonClick}
          onQuickAddSubmit={onQuickAddSubmit}
          onChooseOption={onChooseOptionClick}
          getCartItemTokenByProductToken={
            brandCartViewState.getCartItemTokenByProductToken
          }
          onChooseOptionQuantityUpdate={onChooseOptionQuantityUpdateSubmit}
          showModalOnClick
          isBrandPageComposableProductTile
        />
      </Suspense>
    </div>
  );
};

export const BrandCartContextAwareQuickAdd = ({
  data,
}: {
  data: QuickAddProps;
}) => {
  return (
    <QuickAddTrackingEventsContextProvider data={data}>
      <BrandCartContextAwareQuickAddBase data={data} />
    </QuickAddTrackingEventsContextProvider>
  );
};
